import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AuthService } from '@core/services/auth.services';
import { SessionInitializationService } from '@core/services/session-initialization.service';
import { OpenLoadsIn, UserPreferencesService } from '@core/services/user-preferences.service';
import { LayoutService } from '@layout/service/app.layout.service';

/**
 * The user menu, opened from the avatar in the top bar: who is signed in, their own preferences, and Sign out.
 * Preferences here belong to the person, not to a screen, so they follow them to every list and device.
 */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [FormsModule, SelectButtonModule],
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMenuComponent {
  private readonly authService = inject(AuthService);
  private readonly sessionInitService = inject(SessionInitializationService);
  private readonly router = inject(Router);
  protected readonly preferences = inject(UserPreferencesService);
  private readonly layoutService = inject(LayoutService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Asks the host to close the menu (Sign out leaves the screen anyway). */
  readonly closed = output<void>();

  protected readonly user = this.authService.currentUser;
  protected readonly initials = computed(() => {
    const u = this.user();
    return u ? `${u.firstName?.charAt(0) ?? ''}${u.lastName?.charAt(0) ?? ''}` : '';
  });

  protected readonly openInOptions: { label: string; value: OpenLoadsIn }[] = [
    { label: 'In this window', value: 'Tab' },
    { label: 'Browser tab', value: 'Browser' }
  ];

  /** Light or dark: moved here from its own top-bar button (Dan, 2026-10-15). LayoutService keeps the storage. */
  protected readonly colorScheme = signal(this.layoutService.config.colorScheme);
  protected readonly colorSchemeOptions = [
    { label: 'Light', value: 'light', icon: 'pi pi-sun' },
    { label: 'Dark', value: 'dark', icon: 'pi pi-moon' }
  ];

  protected setColorScheme(value: string | null): void {
    if (value && value !== this.layoutService.config.colorScheme) {
      this.layoutService.toggleColorScheme();
    }
    this.colorScheme.set(this.layoutService.config.colorScheme);
  }

  /** The shortcut that opens one load in a new browser tab whatever the preference says. */
  protected readonly newTabShortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘-click' : 'Ctrl+click';

  protected setOpenLoadsIn(value: OpenLoadsIn | null): void {
    // A select button can be clicked off; a preference always has a value.
    if (value) {
      this.preferences.setOpenLoadsIn(value);
    }
  }

  /**
   * Moves keyboard focus into the menu; the host calls it from the popover's (onShow). PrimeNG's own focusOnShow
   * only focuses an `[autofocus]` element, and neither p-selectbutton's host nor its toggle buttons carry one that
   * can take focus, so the first control is focused here instead.
   */
  focusFirstControl(): void {
    const first = this.host.nativeElement.querySelector<HTMLElement>('[role="button"][tabindex="0"], button:not([disabled])');
    setTimeout(() => first?.focus());
  }

  /**
   * Hands focus back to the button that opened the menu when it closes with focus inside it or lost (Esc, Sign out,
   * a choice). A click outside on another control keeps that control's focus.
   */
  returnFocus(trigger: HTMLElement): void {
    const wasInside = this.host.nativeElement.contains(document.activeElement);
    // After the popover has finished removing its panel: focusing earlier is undone when the panel goes.
    setTimeout(() => {
      const active = document.activeElement;
      if (wasInside || !active || active === document.body || !active.isConnected) {
        trigger.focus();
      }
    });
  }

  protected async signOut(): Promise<void> {
    this.closed.emit();
    // In a "Sign in as" tab, signing out ends that session (US 3635); the admin's own tabs stay signed in.
    if (this.authService.isSignedInAs()) {
      await this.sessionInitService.endSignInAs('ended');
      return;
    }
    await this.sessionInitService.destroySession();
    this.router.navigateByUrl('/login');
  }
}
